import values from 'lodash/values';
import {
    fetchFeatures,
    fetchFeature,
    updateFeature,
    createFeature,
    deleteFeature,
    buildRecentFeatures,
    shapeAnalyticsFeature,
} from './features.utils';
import { createFrozenMap, appendKindIfNotProvided } from '@/vuex/utils';
import { isCrossApp } from '@/common/cross-app-countables/cross-app-countables.utils';
import { hasSubscriptionFlag } from '@/data/flags/local-flags';

const ENABLE_MULTI_APP_COUNTABLE = 'enableMultiAppCountable';

let loadAllPromise;

const features = {
    namespaced: true,
    state: {
        error: {},
        fetching: false,
        map: {},
    },
    mutations: {
        setMap (state, { map }) {
            state.map = map;
        },
        setMapAtKey (state, { id, feature }) {
            state.map[id] = feature;
        },
        deleteMapKey (state, { id }) {
            delete state.map[id];
        },
        setError (state, { error }) {
            state.error = error;
        },
        setFetching (state, { fetching }) {
            state.fetching = fetching;
        },
    },
    actions: {
        async loadAll ({ commit, rootGetters }, { reFetch = false } = {}) {
            if (loadAllPromise && reFetch !== true) {
                await loadAllPromise;

                return;
            }

            const isSuperUser = rootGetters['auth/isSuper'];

            commit('setFetching', { fetching: true });
            loadAllPromise = fetchFeatures({ isSuperUser });

            try {
                const { data: features } = await loadAllPromise;
                const featuresWithKind = features.map((feature) => appendKindIfNotProvided(feature, 'Feature'));

                commit('setFetching', { fetching: false });
                commit('setMap', {
                    map: createFrozenMap(featuresWithKind),
                });
            } catch (error) {
                commit('setFetching', { fetching: false });
                commit('setError', { error });
                throw error;
            }
        },
        loadOne ({ commit, getters }, { reFetch = false, ...payload }) {
            const { id } = payload;
            if (!reFetch && getters.featureById(id)) return Promise.resolve();

            commit('setFetching', { fetching: true });

            return fetchFeature(id)
                .then(({ data: feature }) => {
                    appendKindIfNotProvided(feature, 'Feature');

                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', {
                        id: feature.id,
                        feature: Object.freeze(feature),
                    });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        create ({ commit }, { shouldStartProcessing = true, ...payload } = {}) {
            commit('setFetching', { fetching: true });

            return createFeature(payload, shouldStartProcessing)
                .then(({ data: feature }) => {
                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', {
                        id: feature.id,
                        feature: Object.freeze(feature),
                    });

                    return feature;
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        update ({ commit }, { shouldStartProcessing = false, ...payload } = {}) {
            commit('setFetching', { fetching: true });

            return updateFeature(payload, shouldStartProcessing)
                .then(({ data: feature }) => {
                    commit('setFetching', { fetching: false });
                    commit('setMapAtKey', {
                        id: feature.id,
                        feature: Object.freeze(feature),
                    });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
        remove ({ commit }, { id }) {
            commit('setFetching', { fetching: true });

            return deleteFeature(id)
                .then(() => {
                    commit('setFetching', { fetching: false });
                    commit('deleteMapKey', { id });
                })
                .catch((error) => {
                    commit('setFetching', { fetching: false });
                    commit('setError', { error });
                    throw error;
                });
        },
    },
    getters: {
        // Excludes cross-app countables by default; areas that support them opt into the ...IncludingCrossApp getters.
        list: (state) => {
            const all = values(state.map);

            return hasSubscriptionFlag(ENABLE_MULTI_APP_COUNTABLE)
                ? all.filter((feature) => !isCrossApp(feature))
                : all;
        },
        listIncludingCrossApp: (state) => values(state.map),
        crossAppList: (state) =>
            (hasSubscriptionFlag(ENABLE_MULTI_APP_COUNTABLE) ? values(state.map).filter(isCrossApp) : []),
        featureById: (state) => (id) => state.map[id],
        featuresByAppId: (state, getters) => (appId) => getters.list.filter((feature) => feature.appId === appId),
        featuresByAppIdIncludingCrossApp: (state, getters) => (appId) =>
            getters.listIncludingCrossApp.filter(
                (feature) => feature.appId === appId || (Array.isArray(feature.appIds) && feature.appIds.includes(appId)),
            ),
        analyticsFeaturesListWithStars: (state, getters, _rootState, rootGetters) => {
            const deps = {
                isStarred: rootGetters['stars/getStarByItemId'],
                getIconByAppId: rootGetters['apps/getIconByAppId'],
            };

            return getters.list.map((feature) => shapeAnalyticsFeature(feature, deps));
        },
        analyticsFeaturesListWithStarsIncludingCrossApp: (state, getters, _rootState, rootGetters) => {
            const deps = {
                isStarred: rootGetters['stars/getStarByItemId'],
                getIconByAppId: rootGetters['apps/getIconByAppId'],
            };

            return getters.listIncludingCrossApp.map((feature) => shapeAnalyticsFeature(feature, deps));
        },
        recentFeaturesInReporting: (state, _getters, _rootState, rootGetters) =>
            buildRecentFeatures(state, rootGetters, {
                excludeCrossApp: hasSubscriptionFlag(ENABLE_MULTI_APP_COUNTABLE),
            }),
        recentFeaturesInReportingIncludingCrossApp: (state, _getters, _rootState, rootGetters) =>
            buildRecentFeatures(state, rootGetters, { excludeCrossApp: false }),
    },
};

export default features;

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}
